import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { ServiceOptionItem, ServiceSummaryOptionsType } from '../../types';
import { useTheme } from '../../../theme';
import { CdnIcon } from '@shared/ui';
import { isRequestedService, isUnconfirmedService } from '../../../utils';
import ServiceSummaryGroup from '../ServiceSummaryGroup/ServiceSummaryGroup';
import ServiceSummaryNotification from '../ServiceSummaryNotification/ServiceSummaryNotification';
import { ServiceSummaryType } from '../../../features/service-summary/model/types';

export interface Props {
	groupOption: ServiceSummaryOptionsType;
	serviceType?: ServiceSummaryType;
	mode?: 'default' | 'aggregator';
	selectedIds?: string[];
	disabled?: boolean;
	onSelect?: (item: ServiceOptionItem) => void;
}

const ServiceSummaryOption: React.FC<Props> = ({
	groupOption,
	mode = 'default',
	serviceType,
	selectedIds,
	disabled,
	onSelect
}) => {
	const { t } = useTranslation('Cart');
	const { ServiceSummaryOption: css } = useTheme('ServiceSummary');

	const { items, direction, allSegments, header, subheader, description } = groupOption;

	const services: ServiceOptionItem[] = [];
	const unconfirmedServices: ServiceOptionItem[] = [];
	const requestedServices: ServiceOptionItem[] = [];

	let isLoading = false;

	items.forEach(item => {
		const status = item.status;

		if (isUnconfirmedService(status)) {
			unconfirmedServices.push(item);
		} else if (isRequestedService(status)) {
			requestedServices.push(item);
		} else {
			services.push(item);
		}

		if (item.isLoading && !isLoading) {
			isLoading = true;
		}
	});

	const hasServices = services.length > 0;
	const hasUnconfirmedServices = unconfirmedServices.length > 0;
	const hasRequestedServices = requestedServices.length > 0;

	const segmentTitle = useMemo(() => {
		if (allSegments) {
			return t('All flights');
		} else if (direction) {
			return `${direction.departure.airport.iata} - ${direction.arrival.airport.iata}`;
		}

		return '';
	}, [allSegments, direction]);

	return (
		<div
			className={cn(css.wrapper, {
				[css.unconfirmedServices]: hasUnconfirmedServices,
				[css.loading]: isLoading
			})}
			data-description={description || ''}
		>
			{header && (
				<div className={css.header}>
					<span className={css.header__title}>{header}</span>
					{segmentTitle && <span className={css.header__direction}>{segmentTitle}</span>}
					{hasUnconfirmedServices && (
						<div className={css.header__icon}>
							<CdnIcon icon="Info/Warning" />
						</div>
					)}
				</div>
			)}

			{subheader && <div className={css.subheader}>{subheader}</div>}

			{hasServices && (
				<ServiceSummaryGroup
					mode={mode}
					services={services}
					serviceType={serviceType}
					selectedIds={selectedIds}
					disabled={disabled}
					onSelect={onSelect}
				/>
			)}

			{hasUnconfirmedServices && (
				<ServiceSummaryGroup
					services={unconfirmedServices}
					serviceType={serviceType}
					selectedIds={selectedIds}
					disabled={disabled}
					onSelect={onSelect}
					notification={<ServiceSummaryNotification status="unconfirmed" />}
				/>
			)}

			{hasRequestedServices && (
				<ServiceSummaryGroup
					services={requestedServices}
					serviceType={serviceType}
					selectedIds={selectedIds}
					disabled={disabled}
					onSelect={onSelect}
					notification={<ServiceSummaryNotification status="requested" />}
				/>
			)}
		</div>
	);
};

export default ServiceSummaryOption;
